import {
  Button,
  Checkbox,
  Input,
  Label,
  Switch,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectIcon,
  SelectList,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
  Skeleton,
  Badge,
  type BadgeProps,
} from "raft-ui";
import { DeviceAnalytics } from "../components/DeviceAnalytics";
import { ReleaseHealth } from "../components/ReleaseHealth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ConfirmActionDialog, TypedConfirmField } from "../components/ConfirmActionDialog";
import {
  archiveApp,
  getAuthMe,
  listApps,
  listChannels,
  listBuilds,
  createChannel,
  updateChannel,
  deleteChannel,
  updateApp,
  type App,
  type Channel,
  uploadAppIcon,
  publicAppIconUrl,
  updateAppPublicHistory,
  updateAppHistoryChannels,
  updateAppHistoryDownloads,
  updateAppDeltaUpdates,
  updateAppReleaseApproval,
  getAppClientKey,
  getFeedbackBuildFilter,
  setFeedbackBuildFilter,
  rotateAppClientKey,
  purgeApp,
  listOrgs,
  transferApp,
  getAppStoreReview,
  connectRaftBot,
  createRaftNotification,
  deleteRaftNotification,
  getRaftNotificationConfig,
  listRaftNotificationFailures,
  retryRaftNotificationFailure,
  testRaftNotification,
  updateRaftNotification,
  WEBHOOK_EVENT_TYPES,
  type WebhookEventType,
  type RaftNotificationSubscription,
  addDeviceGroupMember,
  createDeviceGroup,
  deleteDeviceGroup,
  listDeviceGroups,
  removeDeviceGroupMember,
  updateDeviceGroup,
  type DeviceGroup,
} from "../lib/api";
import { useToast } from "../components/Toast";
import { Operations } from "./Operations";
import { deviceGroupUpdatePayload } from "../lib/deviceGroupForm";

export function AppDetail({ appId }: { appId: string }) {
  const apps = useQuery({ queryKey: ["apps"], queryFn: listApps });
  const app = apps.data?.apps.find((a) => a.id === appId);
  const channels = useQuery({
    queryKey: ["channels", appId],
    queryFn: () => listChannels(appId),
  });
  const appMissing = !apps.isLoading && !apps.error && !app;

  return (
    <div>
      {apps.error && (
        <AppErrorBanner
          title="Cannot load apps"
          error={apps.error}
        />
      )}
      {appMissing && (
        <AppErrorBanner
          title="App is not available"
          description="This app is not visible to the current account, or it no longer exists."
        />
      )}
      {channels.error && (
        <AppErrorBanner
          title="Cannot load app details"
          error={channels.error}
        />
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold">App overview</h2>
            <p className="text-xs text-foreground-muted">
              Releases are managed from the{" "}
              <a href={`/apps/${appId}/releases`} className="underline">
                Releases
              </a>{" "}
              tab.
            </p>
          </div>
          {channels.data?.channels.length ? (
            <Button
              variant="primary"
              className="text-sm"
              render={<a href={`/apps/${appId}/releases`} />}
            >
              Publish release →
            </Button>
          ) : (
            <Button
              variant="primary"
              className="text-sm"
              render={<a href={`/apps/${appId}/channels`} />}
            >
              Create channel first
            </Button>
          )}
        </div>
      </section>

      <section className="mt-6">
        <ReleaseHealth appId={appId} />
        <DeviceAnalytics appId={appId} />
      </section>

      <section className="mt-8">
        <Operations appId={appId} />
      </section>
    </div>
  );
}

function AppErrorBanner({
  title,
  description,
  error,
}: {
  title: string;
  description?: string;
  error?: unknown;
}) {
  return (
    <div className="mb-4 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger-strong">
      <div className="font-medium">{title}</div>
      <div className="text-xs mt-1">
        {description ?? (error instanceof Error ? error.message : String(error))}
      </div>
    </div>
  );
}

export function AppChannels({ appId }: { appId: string }) {
  const qc = useQueryClient();
  const apps = useQuery({ queryKey: ["apps"], queryFn: listApps });
  const app = apps.data?.apps.find((a) => a.id === appId);
  const channels = useQuery({
    queryKey: ["channels", appId],
    queryFn: () => listChannels(appId),
  });
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);

  return (
    <div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Channels</h2>
          <Button
            variant="outline"
            onClick={() => setShowCreateChannel(true)}
          >
            + New channel
          </Button>
        </div>
        {channels.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}
        <div className="space-y-4">
          {channels.data?.channels.length === 0 && (
            <EmptyState>
              <EmptyStateTitle>No channels yet.</EmptyStateTitle>
            </EmptyState>
          )}
          {channels.data?.channels.map((c) => (
            <ChannelRow
              key={c.id}
              channel={c}
              onEdit={() => setEditingChannel(c)}
              busy={false}
            />
          ))}
        </div>
        {showCreateChannel && (
          <CreateChannelDialog
            appId={appId}
            onClose={() => setShowCreateChannel(false)}
            onCreated={() => {
              setShowCreateChannel(false);
              qc.invalidateQueries({ queryKey: ["channels", appId] });
            }}
          />
        )}
        {editingChannel && (
          <EditChannelDialog
            appId={appId}
            app={app}
            channel={editingChannel}
            onClose={() => setEditingChannel(null)}
            onSaved={() => {
              setEditingChannel(null);
              qc.invalidateQueries({ queryKey: ["channels", appId] });
            }}
          />
        )}
      </section>
    </div>
  );
}

function AppNamePanel({ appId, app }: { appId: string; app: App }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(app.name);
  const rename = useMutation({
    mutationFn: () => updateApp(appId, { name: name.trim() }),
    onSuccess: () => {
      toast.show({ kind: "success", title: "App renamed" });
      qc.invalidateQueries({ queryKey: ["apps"] });
    },
    onError: (e) =>
      toast.show({ kind: "error", title: "Rename failed", description: (e as Error).message }),
  });
  const dirty = name.trim().length > 0 && name.trim() !== app.name;
  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">App name</div>
        <div className="text-xs text-foreground-muted">
          Display name shown in the console and on share pages. The slug (
          <span className="font-mono">{app.slug}</span>) is permanent — SDKs
          and CI reference it.
        </div>
      </div>
      <Input
        className="h-8! w-full md:w-56 text-sm!"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty && !rename.isPending) rename.mutate();
        }}
      />
      <Button
        variant="primary"
        loading={rename.isPending}
        disabled={!dirty || rename.isPending}
        onClick={() => rename.mutate()}
      >
        Save
      </Button>
    </div>
  );
}

function AppDescriptionPanel({ appId, app }: { appId: string; app: App }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [description, setDescription] = useState(app.description ?? "");
  const save = useMutation({
    mutationFn: () => updateApp(appId, { description: description.trim() || null }),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Description saved" });
      qc.invalidateQueries({ queryKey: ["apps"] });
    },
    onError: (e) =>
      toast.show({ kind: "error", title: "Save failed", description: (e as Error).message }),
  });
  const dirty = description.trim() !== (app.description ?? "");
  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">Description</div>
        <div className="text-xs text-foreground-muted">
          Internal note about what this app is — shown in the console app
          list, not on public pages.
        </div>
      </div>
      <Textarea
        className="w-full resize-y md:w-72 h-16"
        value={description}
        placeholder="What is this app?"
        onChange={(e) => setDescription(e.target.value)}
      />
      <Button
        variant="primary"
        loading={save.isPending}
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate()}
      >
        Save
      </Button>
    </div>
  );
}

function ClientKeyPanel({ appId }: { appId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [revealed, setRevealed] = useState(false);
  const keyQuery = useQuery({
    queryKey: ["client-key", appId],
    queryFn: () => getAppClientKey(appId),
  });
  const rotate = useMutation({
    mutationFn: () => rotateAppClientKey(appId),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Client key rotated — update client configs" });
      qc.invalidateQueries({ queryKey: ["client-key", appId] });
    },
    onError: (e) =>
      toast.show({ kind: "error", title: "Rotate failed", description: (e as Error).message }),
  });
  const key = keyQuery.data?.client_key ?? null;
  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">Client key</div>
        <div className="text-xs text-foreground-muted">
          Required on feedback/crash submissions (X-Hands-Client-Key; legacy
          X-Quiver-Client-Key still accepted). Embedded in client builds; rotate if leaked.
        </div>
        {key && revealed && (
          <div className="mt-1 font-mono text-xs break-all">{key}</div>
        )}
        {!key && !keyQuery.isLoading && (
          <div className="mt-1 text-xs text-warning-strong">
            No key set — submissions are currently unauthenticated. Rotate to generate one.
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 md:contents">
        {key && (
          <>
            <Button
              variant="outline"
              onClick={() => setRevealed((v) => !v)}
            >
              {revealed ? "Hide" : "Reveal"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard?.writeText(key);
                toast.show({ kind: "success", title: "Client key copied" });
              }}
            >
              Copy
            </Button>
          </>
        )}
        <Button
          variant="outline"
          disabled={rotate.isPending}
          onClick={() => {
            if (window.confirm("Rotate the client key? Older client builds stop reporting until they carry the new key.")) {
              rotate.mutate();
            }
          }}
        >
          {rotate.isPending ? "…" : key ? "Rotate" : "Generate"}
        </Button>
      </div>
    </div>
  );
}

function DeviceGroupsPanel({ appId }: { appId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const groups = useQuery({
    queryKey: ["device-groups", appId],
    queryFn: () => listDeviceGroups(appId),
  });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const create = useMutation({
    mutationFn: () => createDeviceGroup(appId, {
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
    }),
    onSuccess: () => {
      setName("");
      setDescription("");
      void queryClient.invalidateQueries({ queryKey: ["device-groups", appId] });
      toast.show({ kind: "success", title: "Device group created" });
    },
    onError: (error) => toast.show({ kind: "error", title: "Create failed", description: (error as Error).message }),
  });

  return (
    <div className="border-t border-line-hairline pt-3 space-y-3">
      <div>
        <div className="text-sm font-medium">Device groups</div>
        <p className="text-xs text-foreground-muted">
          Exact rollout groups use the stable installation device id sent by the Hands update SDK.
        </p>
      </div>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
        <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Artin test devices" />
        <Input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Operator note (optional)" />
        <Button variant="outline" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
          Create group
        </Button>
      </div>
      {groups.isLoading && <p className="text-xs text-foreground-muted">Loading device groups…</p>}
      {groups.error && <p className="text-xs text-danger">{(groups.error as Error).message}</p>}
      {(groups.data?.groups ?? []).map((group) => (
        <DeviceGroupCard key={group.id} appId={appId} group={group} />
      ))}
    </div>
  );
}

function DeviceGroupCard({ appId, group }: { appId: string; group: DeviceGroup }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [deviceId, setDeviceId] = useState("");
  const [label, setLabel] = useState("");
  const [editing, setEditing] = useState(false);
  const [groupName, setGroupName] = useState(group.name);
  const [groupDescription, setGroupDescription] = useState(group.description ?? "");
  useEffect(() => {
    if (!editing) {
      setGroupName(group.name);
      setGroupDescription(group.description ?? "");
    }
  }, [editing, group.description, group.name]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["device-groups", appId] });
  const update = useMutation({
    mutationFn: () => updateDeviceGroup(appId, group.id, deviceGroupUpdatePayload(groupName, groupDescription)),
    onSuccess: () => {
      setEditing(false);
      void refresh();
      toast.show({ kind: "success", title: "Device group updated" });
    },
    onError: (error) => toast.show({ kind: "error", title: "Update failed", description: (error as Error).message }),
  });
  const add = useMutation({
    mutationFn: () => addDeviceGroupMember(appId, group.id, {
      device_id: deviceId.trim(),
      ...(label.trim() ? { label: label.trim() } : {}),
    }),
    onSuccess: () => {
      setDeviceId("");
      setLabel("");
      void refresh();
      toast.show({ kind: "success", title: "Device added" });
    },
    onError: (error) => toast.show({ kind: "error", title: "Add failed", description: (error as Error).message }),
  });
  const remove = useMutation({
    mutationFn: (memberDeviceId: string) => removeDeviceGroupMember(appId, group.id, memberDeviceId),
    onSuccess: () => void refresh(),
    onError: (error) => toast.show({ kind: "error", title: "Remove failed", description: (error as Error).message }),
  });
  const destroy = useMutation({
    mutationFn: () => deleteDeviceGroup(appId, group.id),
    onSuccess: () => {
      void refresh();
      toast.show({ kind: "success", title: "Device group deleted" });
    },
    onError: (error) => toast.show({ kind: "error", title: "Delete failed", description: (error as Error).message }),
  });

  return (
    <div className="rounded-md bg-fill-muted p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          {editing ? (
            <div className="space-y-2">
              <Input value={groupName} onChange={(event) => setGroupName(event.target.value)} placeholder="Group name" />
              <Input
                value={groupDescription}
                onChange={(event) => setGroupDescription(event.target.value)}
                placeholder="Operator note"
              />
            </div>
          ) : (
            <>
              <div className="text-sm font-medium">{group.name}</div>
              {group.description && <div className="text-xs text-foreground-muted">{group.description}</div>}
            </>
          )}
          <div className="text-[11px] font-mono text-foreground-hint">{group.id}</div>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" disabled={!groupName.trim() || update.isPending} onClick={() => update.mutate()}>
                Save
              </Button>
              <Button variant="outline" disabled={update.isPending} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => setEditing(true)}>Edit</Button>
          )}
          <Button
            variant="outline"
            disabled={destroy.isPending || editing}
            onClick={() => { if (window.confirm(`Delete device group '${group.name}'?`)) destroy.mutate(); }}
          >
            Delete
          </Button>
        </div>
      </div>
      <div className="space-y-1">
        {group.members.length === 0 && <p className="text-xs text-foreground-muted">No devices yet.</p>}
        {group.members.map((member) => (
          <div key={member.device_id} className="flex items-center justify-between gap-2 text-xs">
            <span className="min-w-0 truncate">
              <span className="font-medium">{member.label || "Device"}</span>{" "}
              <span className="font-mono text-foreground-muted">{member.device_id}</span>
            </span>
            <Button variant="outline" onClick={() => remove.mutate(member.device_id)} disabled={remove.isPending}>
              Remove
            </Button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-[1fr_160px_auto] gap-2">
        <Input value={deviceId} onChange={(event) => setDeviceId(event.target.value)} placeholder="Installation device id" />
        <Input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Label" />
        <Button variant="outline" disabled={!deviceId.trim() || add.isPending} onClick={() => add.mutate()}>
          Add
        </Button>
      </div>
    </div>
  );
}

// --- App Store review status (read-only) ---------------------------------

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

/** Map an appStoreState enum to a Badge variant + a human label. */
function appStoreStateBadge(state: string | null): {
  variant: BadgeVariant;
  label: string;
} {
  switch (state) {
    case "READY_FOR_SALE":
      return { variant: "success", label: "Ready for sale" };
    case "IN_REVIEW":
      return { variant: "information", label: "In review" };
    case "WAITING_FOR_REVIEW":
      return { variant: "warning", label: "Waiting for review" };
    case "PENDING_DEVELOPER_RELEASE":
      return { variant: "accent", label: "Pending developer release" };
    case "PENDING_APPLE_RELEASE":
      return { variant: "accent", label: "Pending Apple release" };
    case "PREPARE_FOR_SUBMISSION":
      return { variant: "muted", label: "Preparing" };
    case "REJECTED":
      return { variant: "danger", label: "Rejected" };
    case "METADATA_REJECTED":
      return { variant: "danger", label: "Metadata rejected" };
    case "DEVELOPER_REJECTED":
      return { variant: "danger", label: "Developer rejected" };
    default:
      // Unknown/other states (e.g. PROCESSING_FOR_APP_STORE): show verbatim.
      return {
        variant: "muted",
        label: state ? state.replace(/_/g, " ").toLowerCase() : "Unknown",
      };
  }
}

/** Map a TestFlight betaReviewState to a Badge variant + label. */
function betaReviewStateBadge(state: string | null): {
  variant: BadgeVariant;
  label: string;
} {
  switch (state) {
    case "APPROVED":
      return { variant: "success", label: "Approved" };
    case "IN_REVIEW":
      return { variant: "information", label: "In review" };
    case "WAITING_FOR_REVIEW":
      return { variant: "warning", label: "Waiting for review" };
    case "REJECTED":
      return { variant: "danger", label: "Rejected" };
    default:
      return { variant: "muted", label: "No beta review" };
  }
}

export function AppStoreReviewPanel({ appId }: { appId: string; app: App }) {
  // Non-iOS apps never render this panel — the parent guards on
  // app.platform === "ios" (mirrors the worker's applicable:false response).
  const review = useQuery({
    queryKey: ["appstore-review", appId],
    queryFn: () => getAppStoreReview(appId),
  });

  const data = review.data;
  const versions = data?.app_store_versions ?? [];
  const builds = data?.testflight_builds ?? [];
  const current = versions[0];
  const currentBadge = current ? appStoreStateBadge(current.appStoreState) : null;

  return (
    <div className="border-t border-line-hairline pt-3">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium">App Store review status</div>
            {currentBadge && (
              <Badge variant={currentBadge.variant}>{currentBadge.label}</Badge>
            )}
          </div>
          <div className="text-xs text-foreground-muted">
            Live review state of this app's recent App Store versions and
            TestFlight builds, from App Store Connect. Read-only.
          </div>
        </div>
      </div>

      <div className="mt-3">
        {review.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : data?.configured === false ? (
          <EmptyState>
            <EmptyStateTitle>App Store Connect not configured</EmptyStateTitle>
            <EmptyStateDescription>
              Add ASC credentials in the TestFlight settings above to see review
              status.
            </EmptyStateDescription>
          </EmptyState>
        ) : data?.bundle_id === null ? (
          <EmptyState>
            <EmptyStateTitle>No App Store bundle ID configured</EmptyStateTitle>
            <EmptyStateDescription>
              Set the production App Store bundle ID on the{" "}
              <a
                href={`/apps/${appId}/channels`}
                className="underline font-medium"
              >
                main channel
              </a>{" "}
              (Channels tab). Only the main channel's bundle ID is used —
              preview/nightly channels are ignored, so their beta bundle IDs won't
              be picked up here.
            </EmptyStateDescription>
          </EmptyState>
        ) : data?.error ? (
          <div className="rounded-md bg-warning-soft px-3 py-2 text-xs text-warning-strong">
            Could not load review status from App Store Connect: {data.error}
            {data.bundle_id && (
              <div className="mt-1">
                Bundle ID{" "}
                <span className="font-mono">{data.bundle_id}</span> is set on the
                main channel. If it doesn't match your App Store app, update it in{" "}
                <a
                  href={`/apps/${appId}/channels`}
                  className="underline font-medium"
                >
                  Channels
                </a>
                .
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-xs font-medium text-foreground-muted mb-1">
                Recent App Store versions
              </div>
              {versions.length === 0 ? (
                <div className="text-xs text-foreground-hint">
                  No App Store versions yet.
                </div>
              ) : (
                <ul className="space-y-1">
                  {versions.map((v, i) => {
                    const b = appStoreStateBadge(v.appStoreState);
                    return (
                      <li
                        key={`${v.versionString}-${i}`}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span className="font-mono">
                          {v.versionString ?? "—"}
                        </span>
                        <Badge variant={b.variant}>{b.label}</Badge>
                        {(v.appStoreState === "REJECTED" ||
                          v.appStoreState === "METADATA_REJECTED" ||
                          v.appStoreState === "DEVELOPER_REJECTED") && (
                          <span className="text-xs text-foreground-hint">
                            (see App Store Connect for the rejection details)
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div>
              <div className="text-xs font-medium text-foreground-muted mb-1">
                Recent TestFlight builds — beta review
              </div>
              {builds.length === 0 ? (
                <div className="text-xs text-foreground-hint">
                  No builds on App Store Connect yet.
                </div>
              ) : (
                <ul className="space-y-1">
                  {builds.map((bld, i) => {
                    const b = betaReviewStateBadge(bld.betaReviewState);
                    return (
                      <li
                        key={`${bld.version}-${i}`}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span className="font-mono">{bld.version ?? "—"}</span>
                        <Badge variant={b.variant}>{b.label}</Badge>
                        {bld.processingState &&
                          bld.processingState !== "VALID" && (
                            <span className="text-xs text-foreground-hint">
                              {bld.processingState.toLowerCase()}
                            </span>
                          )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PublicHistoryToggle({ appId, app }: { appId: string; app: App }) {
  const toast = useToast();
  const qc = useQueryClient();
  const enabled = Boolean(app.public_history);
  const channelsQuery = useQuery({
    queryKey: ["channels", appId],
    queryFn: () => listChannels(appId),
  });
  // history_channels is a JSON array of slugs; null/empty = all channels shown.
  const selectedChannels: string[] = (() => {
    try {
      const parsed = app.history_channels ? JSON.parse(app.history_channels) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();
  const showDownloads = app.history_show_downloads !== 0;
  const channelsMutation = useMutation({
    mutationFn: (slugs: string[]) => updateAppHistoryChannels(appId, slugs.length ? slugs : null),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Visible channels updated" });
      qc.invalidateQueries({ queryKey: ["apps"] });
    },
    onError: (e) => toast.show({ kind: "error", title: "Update failed", description: (e as Error).message }),
  });
  const downloadsToggle = useMutation({
    mutationFn: () => updateAppHistoryDownloads(appId, !showDownloads),
    onSuccess: () => {
      toast.show({
        kind: "success",
        title: !showDownloads ? "Downloads shown on public pages" : "Downloads hidden on public pages",
      });
      qc.invalidateQueries({ queryKey: ["apps"] });
    },
    onError: (e) => toast.show({ kind: "error", title: "Update failed", description: (e as Error).message }),
  });
  const toggleChannel = (slug: string) => {
    const next = selectedChannels.includes(slug)
      ? selectedChannels.filter((s) => s !== slug)
      : [...selectedChannels, slug];
    channelsMutation.mutate(next);
  };
  const toggle = useMutation({
    mutationFn: () => updateAppPublicHistory(appId, !enabled),
    onSuccess: () => {
      toast.show({
        kind: "success",
        title: !enabled ? "Public version history enabled" : "Public version history disabled",
      });
      qc.invalidateQueries({ queryKey: ["apps"] });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Update failed",
        description: (e as Error).message,
      }),
  });
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="text-sm font-medium">Public version history</div>
          <div className="text-xs text-foreground-muted">
            {enabled ? (
              <>
                Anyone can browse and download published versions at{" "}
                <a
                  className="underline"
                  href={`/apps/${app.slug}/history`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  /apps/{app.slug}/history
                </a>
                {" · latest landing: "}
                <a
                  className="underline"
                  href={`/apps/${app.slug}/latest`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  /apps/{app.slug}/latest
                </a>
                .
              </>
            ) : (
              "Expose a public page listing published versions with changelogs and downloads."
            )}
          </div>
        </div>
        <Switch
          checked={enabled}
          disabled={toggle.isPending}
          onCheckedChange={() => toggle.mutate()}
        />
      </div>
      {enabled && (
        <div className="pl-1 space-y-2 text-sm">
          <div className="flex items-center gap-3">
            <div className="flex-1 text-xs text-foreground-muted">
              Channels shown on the public history pages (none selected = all).
            </div>
            <div className="flex flex-wrap gap-2">
              {(channelsQuery.data?.channels ?? []).map((ch) => (
                <label key={ch.id} className="flex items-center gap-1 text-xs">
                  <Checkbox
                    size="sm"
                    checked={selectedChannels.includes(ch.slug)}
                    disabled={channelsMutation.isPending}
                    onCheckedChange={() => toggleChannel(ch.slug)}
                  />
                  {ch.slug}
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 text-xs text-foreground-muted">
              Show per-version download buttons on the public history, notes and landing pages.
            </div>
            <Switch
              checked={showDownloads}
              disabled={downloadsToggle.isPending}
              onCheckedChange={() => downloadsToggle.mutate()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function DeltaUpdatesToggle({ appId, app }: { appId: string; app: App }) {
  const toast = useToast();
  const qc = useQueryClient();
  const enabled = Boolean(app.delta_updates_enabled);
  const toggle = useMutation({
    mutationFn: () => updateAppDeltaUpdates(appId, !enabled),
    onSuccess: () => {
      toast.show({
        kind: "success",
        title: !enabled ? "Delta updates enabled" : "Delta updates disabled",
      });
      qc.invalidateQueries({ queryKey: ["apps"] });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Update failed",
        description: (e as Error).message,
      }),
  });
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <div className="text-sm font-medium">Delta updates</div>
        <div className="text-xs text-foreground-muted">
          {enabled
            ? "On publish, small differential patches are generated from recent versions so users download less to update."
            : "Auto-generate differential update patches when a release is published, shrinking update downloads for users on recent versions."}
        </div>
      </div>
      <Switch
        checked={enabled}
        disabled={toggle.isPending}
        onCheckedChange={() => toggle.mutate()}
      />
    </div>
  );
}

function ReleaseApprovalToggle({ appId, app }: { appId: string; app: App }) {
  const toast = useToast();
  const qc = useQueryClient();
  const enabled = Boolean(app.release_requires_human_approval);
  const toggle = useMutation({
    mutationFn: () => updateAppReleaseApproval(appId, !enabled),
    onSuccess: () => {
      toast.show({
        kind: "success",
        title: !enabled ? "Human approval required" : "Human approval not required",
      });
      qc.invalidateQueries({ queryKey: ["apps"] });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Update failed",
        description: (e as Error).message,
      }),
  });
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <div className="text-sm font-medium">Require human approval for agent releases</div>
        <div className="text-xs text-foreground-muted">
          {enabled
            ? "On. Releases that an agent (deploy token) tries to publish are held for an app admin or org admin to approve or reject before they go live. Releases you publish from this console are unaffected."
            : "When on, agent-initiated releases are held for a human (app admin / org admin) to approve before publishing. Your own console publishes are never gated."}
        </div>
      </div>
      <Switch
        checked={enabled}
        disabled={toggle.isPending}
        onCheckedChange={() => toggle.mutate()}
      />
    </div>
  );
}

function AppIconUploader({ appId, slug }: { appId: string; slug: string }) {
  const toast = useToast();
  const [bust, setBust] = useState(0);
  const upload = useMutation({
    mutationFn: (file: File) => uploadAppIcon(appId, file),
    onSuccess: () => {
      toast.show({ kind: "success", title: "App icon updated" });
      setBust(Date.now());
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Icon upload failed",
        description: (e as Error).message,
      }),
  });
  return (
    <div className="flex flex-wrap items-center gap-3">
      <img
        src={`${publicAppIconUrl(slug)}${bust ? `?v=${bust}` : ""}`}
        alt=""
        width={44}
        height={44}
        className="h-11 w-11 rounded-lg border border-line-muted bg-layer-inset object-cover"
        onError={(e) => {
          (e.target as HTMLImageElement).style.visibility = "hidden";
        }}
        onLoad={(e) => {
          (e.target as HTMLImageElement).style.visibility = "visible";
        }}
      />
      <div className="min-w-0">
        <div className="text-sm font-medium">App icon</div>
        <div className="text-xs text-foreground-muted">
          Shown on share/download pages. PNG/WebP/JPEG, max 1MB.
        </div>
      </div>
      <Button variant="outline" size="xs" className="ml-auto" render={<label className="cursor-pointer" />}>
        {upload.isPending ? "Uploading…" : "Upload"}
        <input
          type="file"
          accept="image/png,image/webp,image/jpeg"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
            e.currentTarget.value = "";
          }}
        />
      </Button>
    </div>
  );
}

function FeedbackBuildFilterToggle({ appId }: { appId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const setting = useQuery({ queryKey: ["feedback-build-filter", appId], queryFn: () => getFeedbackBuildFilter(appId) });
  const enabled = setting.data?.default_enabled === 1;
  const update = useMutation({
    mutationFn: (next: boolean) => setFeedbackBuildFilter(appId, next),
    onSuccess: (_, next) => {
      toast.show({ kind: "success", title: next ? "External report separation enabled" : "External report separation disabled" });
      qc.invalidateQueries({ queryKey: ["feedback-build-filter", appId] });
      qc.invalidateQueries({ queryKey: ["feedback", appId] });
      qc.invalidateQueries({ queryKey: ["feedback-stats", appId] });
      qc.invalidateQueries({ queryKey: ["crash-groups", appId] });
      qc.invalidateQueries({ queryKey: ["error-groups", appId] });
    },
    onError: (e) => toast.show({ kind: "error", title: "Update failed", description: (e as Error).message }),
  });
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <div className="text-sm font-medium">Separate external iOS reports</div>
        <div className="text-xs text-foreground-muted">
          Compare reported bundle IDs with this app's configured channels. Mismatches appear in the external view and do not create normal notification cards; unknown IDs stay in the main view. Bundle IDs are supplied by clients and do not verify identity.
        </div>
        {setting.error && <div className="text-xs text-danger">Could not load this setting.</div>}
      </div>
      <Switch aria-label="Separate external iOS reports" checked={enabled}
        disabled={!setting.data || update.isPending} onCheckedChange={(next) => update.mutate(next)} />
    </div>
  );
}

export function AppSettings({ appId }: { appId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const apps = useQuery({ queryKey: ["apps"], queryFn: listApps });
  const app = apps.data?.apps.find((a) => a.id === appId);
  const me = useQuery({ queryKey: ["auth-me"], queryFn: () => getAuthMe() });
  const orgRole = me.data?.account.org_role ?? null;
  const isOrgAdmin = orgRole === "owner" || orgRole === "admin";
  const orgs = useQuery({ queryKey: ["orgs"], queryFn: listOrgs });

  const [confirmArchive, setConfirmArchive] = useState(false);

  // Owns the typed-confirmation state for the purge gate. ConfirmActionDialog renders the
  // field but deliberately does not hold state, so the caller supplies value/onChange and
  // enforces the match itself (see TypedConfirmField's doc comment).
  const [purgeTyped, setPurgeTyped] = useState("");
  const [confirmPurge, setConfirmPurge] = useState(false);
  const purge = useMutation({
    // Send what the operator actually typed. Passing `app.slug` made the gate decorative -
    // the caller never had to reproduce the slug, so it could not stop a misclick.
    mutationFn: () => purgeApp(appId, purgeTyped),
    onSuccess: (res) => {
      toast.show({
        kind: "success",
        title: `App purged (${res.r2_objects_deleted} stored files removed)`,
      });
      setConfirmPurge(false);
      qc.invalidateQueries({ queryKey: ["apps"] });
      window.location.assign("/apps");
    },
    onError: (e) =>
      toast.show({ kind: "error", title: "Purge failed", description: (e as Error).message }),
  });

  const archive = useMutation({
    mutationFn: (archived: boolean) =>
      archiveApp(appId, { archived }),
    onSuccess: (_, archived) => {
      toast.show({
        kind: "success",
        title: archived ? "App archived" : "App restored",
      });
      qc.invalidateQueries({ queryKey: ["apps"] });
      qc.invalidateQueries({ queryKey: ["builds", appId] });
      qc.invalidateQueries({ queryKey: ["releases", appId] });
      setConfirmArchive(false);
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Archive failed",
        description: (e as Error).message,
      }),
  });

  if (!app) return null;

  return (
    <div>

    <div className="space-y-3 text-sm">
        <h2 className="text-sm font-medium text-foreground-strong">Settings</h2>

        <AppTransferPanel appId={appId} app={app} orgs={orgs.data?.orgs ?? []} />

        {/* App name (slug is immutable) */}
        <AppNamePanel appId={appId} app={app} />

        {/* App description */}
        <AppDescriptionPanel appId={appId} app={app} />

        {/* App icon */}
        <AppIconUploader appId={appId} slug={app.slug} />

        {/* Public version history */}
        <PublicHistoryToggle appId={appId} app={app} />

        {/* Delta/differential updates — Android apps only */}
        {app.platform === "android" && <DeltaUpdatesToggle appId={appId} app={app} />}

        {/* Release human-approval gate (task #239) — mobile platforms */}
        {["ios", "android", "ohos"].includes(app.platform) && (
          <ReleaseApprovalToggle appId={appId} app={app} />
        )}

        {app.platform === "ios" && <FeedbackBuildFilterToggle appId={appId} />}

        {/* Client key (feedback/crash reporting auth) */}
        <ClientKeyPanel appId={appId} />

        {/* Exact per-installation rollout groups */}
        <DeviceGroupsPanel appId={appId} />

        {/* TestFlight upload credentials — iOS apps only */}

        {/* Hands-managed Raft App Bot notifications */}
        <RaftNotificationsPanel appId={appId} />

        {/* Default release channel picker */}
        <DefaultChannelPicker appId={appId} app={app} isOrgAdmin={isOrgAdmin} />

        {/* Danger zone: archive / restore */}
        <div className="border-t border-line-hairline pt-3">
          <h3 className="text-sm font-medium text-foreground-strong mb-2">
            Danger zone
          </h3>
          {!isOrgAdmin && (
            <p className="text-xs text-warning-strong mb-2">
              ⚠ Org owner / admin required to archive apps.
            </p>
          )}

          <div className="flex items-center justify-between gap-2 rounded-md bg-fill-muted p-3">
            <div>
              <div className="font-medium">
                {app.archived ? "App is archived" : "App is active"}
              </div>
              <div className="text-xs text-foreground-muted">
                {app.archived
                  ? "Archived apps reject new uploads but remain restorable. " +
                    "Restore the app to resume normal operation."
                  : "Active apps accept uploads + releases normally."}
              </div>
              {app.archived_at && (
                <div className="text-xs text-foreground-hint mt-1">
                  archived_at: {new Date(app.archived_at).toISOString()}
                </div>
              )}
            </div>
            {isOrgAdmin && (
              <div className="flex flex-col items-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmArchive(true)}
                  disabled={archive.isPending}
                >
                  {app.archived ? "Restore app" : "Archive app"}
                </Button>
                {Boolean(app.archived) && (
                  <Button
                    variant="danger"
                    disabled={purge.isPending}
                    onClick={() => setConfirmPurge(true)}
                  >
                    Purge permanently
                  </Button>
                )}
              </div>
            )}
          </div>

          <ConfirmActionDialog
            open={confirmPurge}
            title="Purge this app permanently?"
            objectLabel={app.name ?? app.slug ?? app.id}
            objectHint={`slug: ${app.slug}`}
            body={
              <>
                Deletes the app and <strong>everything it owns</strong> — builds,
                releases, share links, feedback tickets, and uploaded files.{" "}
                <strong>This cannot be undone.</strong>
              </>
            }
            confirmLabel="Purge permanently"
            confirmKind="danger"
            requiredText={app.slug}
            typedValue={purgeTyped}
            onTypedChange={setPurgeTyped}
            confirmDisabled={purgeTyped !== app.slug}
            pending={purge.isPending}
            onConfirm={() => purge.mutate()}
            onCancel={() => {
              setConfirmPurge(false);
              setPurgeTyped("");
            }}
          />

          <ConfirmActionDialog
            open={confirmArchive}
            title={app.archived ? "Restore this app?" : "Archive this app?"}
            objectLabel={app.name ?? app.slug ?? app.id}
            objectHint={`slug: ${app.slug}`}
            objectSummary={
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono">
                <div className="text-foreground-muted">id</div>
                <div>{app.id.slice(0, 8)}…</div>
                <div className="text-foreground-muted">slug</div>
                <div>{app.slug}</div>
                <div className="text-foreground-muted">status</div>
                <div>{app.archived ? "archived" : "active"}</div>
              </div>
            }
            body={
              app.archived ? (
                <>
                  Restoring returns the app to <strong>active</strong> status.{" "}
                  New uploads and releases will be accepted again.
                  <br />
                  <span className="text-xs text-foreground-muted">
                    Existing builds, releases, and assets are kept as-is.
                  </span>
                </>
              ) : (
                <>
                  Archiving marks the app as <strong>archived</strong>.
                  The app remains viewable in lists and admin pages, but{" "}
                  <strong>new uploads are rejected</strong>.
                  <br />
                  <span className="text-xs text-foreground-muted">
                    This is reversible: existing builds, releases, and assets
                    are kept as-is. Archiving does not free up storage.
                  </span>
                </>
              )
            }
            confirmLabel={app.archived ? "Restore app" : "Archive app"}
            cancelLabel={app.archived ? "Keep archived" : "Keep app"}
            confirmKind={app.archived ? "primary" : "danger"}
            pending={archive.isPending}
            onCancel={() => setConfirmArchive(false)}
            onConfirm={() => {
              archive.mutate(!app.archived);
              setConfirmArchive(false);
            }}
          />
          <p className="text-xs text-foreground-muted mt-2">
            Future: signing credential binding, custom domains, app
            ownership transfer.
          </p>
        </div>
      </div>
    </div>
  );
}

function RaftNotificationsPanel({ appId }: { appId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const config = useQuery({
    queryKey: ["raft-notifications", appId],
    queryFn: () => getRaftNotificationConfig(appId),
  });
  const [channelTarget, setChannelTarget] = useState("");
  const [events, setEvents] = useState<WebhookEventType[]>(WEBHOOK_EVENT_TYPES);

  useEffect(() => {
    if (config.data?.events?.length && events.length === 0) setEvents(config.data.events);
  }, [config.data?.events]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["raft-notifications", appId] });
  const connect = useMutation({
    mutationFn: () => connectRaftBot(appId),
    onSuccess: () => { toast.show({ kind: "success", title: "Hands App Bot connected" }); refresh(); },
    onError: (error) => toast.show({ kind: "error", title: "Could not connect App Bot", description: (error as Error).message }),
  });
  const create = useMutation({
    mutationFn: () => createRaftNotification(appId, { channel_target: channelTarget.trim(), events }),
    onSuccess: () => { toast.show({ kind: "success", title: "Raft notification destination added" }); setChannelTarget(""); refresh(); },
    onError: (error) => toast.show({ kind: "error", title: "Could not add destination", description: (error as Error).message }),
  });
  const update = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) => updateRaftNotification(appId, input.id, { enabled: input.enabled }),
    onSuccess: refresh,
    onError: (error) => toast.show({ kind: "error", title: "Could not update destination", description: (error as Error).message }),
  });
  const updateCards = useMutation({
    mutationFn: (input: { id: string; card_updates: boolean }) => updateRaftNotification(appId, input.id, { card_updates: input.card_updates }),
    onSuccess: (_result, input) => {
      if (!input.card_updates) toast.show({ kind: "success", title: "Text selected", description: "In-flight card requests may still finish; later notifications will use text." });
      refresh();
    },
    onError: (error) => toast.show({ kind: "error", title: "Could not update card settings", description: (error as Error).message }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteRaftNotification(appId, id),
    onSuccess: () => { toast.show({ kind: "success", title: "Raft notification destination removed" }); refresh(); },
    onError: (error) => toast.show({ kind: "error", title: "Could not remove destination", description: (error as Error).message }),
  });
  const test = useMutation({
    mutationFn: (id: string) => testRaftNotification(appId, id),
    onSuccess: () => toast.show({ kind: "success", title: "Test message sent" }),
    onError: (error) => toast.show({ kind: "error", title: "Test message failed", description: (error as Error).message }),
  });

  if (config.isLoading) return <div className="border-t border-line-hairline pt-3 text-xs text-foreground-muted">Loading Raft notifications…</div>;
  if (config.error) return <div className="border-t border-line-hairline pt-3 text-xs text-danger-strong">Could not load Raft notification settings: {(config.error as Error).message}</div>;
  const bot = config.data?.bot;
  const subscriptions = config.data?.subscriptions ?? [];
  const toggleEvent = (event: WebhookEventType) => setEvents((current) => current.includes(event) ? current.filter((item) => item !== event) : [...current, event]);

  return (
    <div className="border-t border-line-hairline pt-3 space-y-3">
      <div>
        <h3 className="text-sm font-medium text-foreground-strong">Raft notifications</h3>
        <p className="text-xs text-foreground-muted mt-1">
          Choose the Raft channel and Hands events that this app should post. The bot key stays encrypted in Hands and is never shown here.
        </p>
        <p className="text-xs text-foreground-muted mt-1">
          A Raft server owner/admin must grant the Hands App Bot. Connect it, add it to the target channel, choose events, and use Test to confirm delivery.{" "}
          <a className="underline" href="/docs/admin-user-guide/#raft-notifications">Raft notification setup guide</a>
        </p>
        <p className="text-xs text-foreground-muted mt-1">
          Feedback notifications can include an excerpt of user-submitted text. TestFlight notifications can include test group names and an excerpt of testing notes. Choose a channel whose members may see them.
        </p>
        <p className="text-xs text-foreground-muted mt-1">
          Release status cards update the same message when App Store, external TestFlight or Google Play status changes. Cards start with new objects; existing text threads stay as text. Updates do not send a new notification.
        </p>
      </div>
      <div className="rounded-md bg-fill-muted p-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs">
          <div className="font-medium">App Bot: {bot?.connected ? "connected" : "not connected"}</div>
          <div className="text-foreground-muted mt-1">
            {bot?.connected ? (bot.handle ? `Handle ${bot.handle}` : "The bot is ready to send.") : "A Raft server owner/admin must grant the bot before connecting."}
          </div>
        </div>
        {!bot?.connected && <Button variant="outline" size="xs" onClick={() => connect.mutate()} disabled={connect.isPending}>{connect.isPending ? "Connecting…" : "Connect App Bot"}</Button>}
      </div>
      {bot?.connected && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-64 flex-1">
              <Label htmlFor={`raft-channel-${appId}`}>Target channel</Label>
              <Input id={`raft-channel-${appId}`} value={channelTarget} onChange={(event) => setChannelTarget(event.target.value)} placeholder="#proj-mobile-release" />
            </div>
            <Button variant="outline" size="xs" onClick={() => create.mutate()} disabled={!channelTarget.trim() || events.length === 0 || create.isPending}>{create.isPending ? "Adding…" : "Add destination"}</Button>
          </div>
          <div>
            <div className="text-xs font-medium mb-1">Events</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
              {WEBHOOK_EVENT_TYPES.map((event) => (
                <label key={event} className="flex items-center gap-2 text-xs text-foreground-muted">
                  <Checkbox checked={events.includes(event)} onCheckedChange={() => toggleEvent(event)} />
                  <span>{event}</span>
                </label>
              ))}
            </div>
          </div>
          {subscriptions.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium">Configured destinations</div>
              {subscriptions.map((subscription) => (
                <RaftNotificationRow key={subscription.id} appId={appId} subscription={subscription} onToggle={(enabled) => update.mutate({ id: subscription.id, enabled })} onToggleCards={(card_updates) => updateCards.mutate({ id: subscription.id, card_updates })} onTest={() => test.mutate(subscription.id)} onDelete={() => { if (window.confirm(`Remove ${subscription.channel_target}?`)) remove.mutate(subscription.id); }} busy={update.isPending || updateCards.isPending || test.isPending || remove.isPending} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RaftNotificationRow({
  appId,
  subscription,
  onToggle,
  onToggleCards,
  onTest,
  onDelete,
  busy,
}: {
  appId: string;
  subscription: RaftNotificationSubscription;
  onToggle: (enabled: boolean) => void;
  onToggleCards: (enabled: boolean) => void;
  onTest: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const failures = useQuery({
    queryKey: ["raft-notification-failures", appId, subscription.id],
    queryFn: () => listRaftNotificationFailures(appId, subscription.id),
  });
  const retry = useMutation({
    mutationFn: (id: string) => retryRaftNotificationFailure(appId, subscription.id, id),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Retry scheduled", description: "Hands will retry when the delivery service next runs." });
      void qc.invalidateQueries({ queryKey: ["raft-notification-failures", appId, subscription.id] });
    },
    onError: (error) => toast.show({ kind: "error", title: "Could not retry notification", description: (error as Error).message }),
  });
  return (
    <div className="rounded-md border border-line-hairline p-2 flex flex-wrap items-center gap-2">
      <div className="min-w-48 flex-1">
        <div className="font-mono text-xs">{subscription.channel_target}</div>
        <div className="text-[11px] text-foreground-muted mt-1">{subscription.events.length} event{subscription.events.length === 1 ? "" : "s"}</div>
      </div>
      <Badge variant={subscription.enabled ? "success" : "muted"}>{subscription.enabled ? "enabled" : "disabled"}</Badge>
      <Button variant="ghost" size="xs" onClick={() => onToggleCards(!subscription.card_updates)} disabled={busy}>{subscription.card_updates ? "Use text" : "Use status cards"}</Button>
      <Button variant="ghost" size="xs" onClick={() => onToggle(!subscription.enabled)} disabled={busy}>{subscription.enabled ? "Disable" : "Enable"}</Button>
      <Button variant="ghost" size="xs" onClick={onTest} disabled={busy || !subscription.enabled}>Test</Button>
      <Button variant="ghost" size="xs" onClick={onDelete} disabled={busy}>Remove</Button>
      {failures.isError && <div className="w-full text-xs text-foreground-muted">Could not load failed notifications.</div>}
      {failures.data?.failures.map((failure) => (
        <div key={failure.id} className="w-full flex flex-wrap items-center gap-2 text-xs text-foreground-muted">
          <span>Failed: {failure.event_type} · {new Date(failure.created_at).toLocaleString()}</span>
          <Button variant="ghost" size="xs" onClick={() => retry.mutate(failure.id)} disabled={busy || retry.isPending || !subscription.enabled}>Retry</Button>
        </div>
      ))}
    </div>
  );
}

function AppTransferPanel({ appId, app, orgs }: { appId: string; app: App; orgs: Array<{ id: string; name: string; slug: string; archived: number }> }) {
  const toast = useToast(); const qc = useQueryClient();
  const [target, setTarget] = useState(""); const [confirm, setConfirm] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const transfer = useMutation({
    mutationFn: () => transferApp(appId, { target_org_id: target, expected_source_org_id: app.org_id ?? "", idempotency_key: idempotencyKey }),
    onSuccess: () => { toast.show({ kind: "success", title: "App transferred" }); setConfirm(""); setIdempotencyKey(crypto.randomUUID()); void qc.invalidateQueries({ queryKey: ["apps"] }); },
    onError: (e) => toast.show({ kind: "error", title: "Transfer failed", description: (e as Error).message }),
  });
  const eligible = orgs.filter((o) => !o.archived && o.id !== app.org_id);
  return <div className="border-t border-line-hairline pt-3"><h3 className="text-sm font-medium text-foreground-strong mb-1">Transfer app</h3><p className="text-xs text-foreground-muted mb-2">Preserves this app ID and all releases, builds, channels, and history. Owners of both organizations must authorize.</p><div className="flex flex-wrap items-center gap-2"><Select value={target || "none"} onValueChange={(value) => setTarget(value === "none" ? "" : String(value))}><SelectTrigger className="w-full sm:w-64" aria-label="Select target organization" disabled={transfer.isPending}><SelectValue /><SelectIcon /></SelectTrigger><SelectContent>
    <SelectList><SelectItem value="none">Select target organization…</SelectItem>{eligible.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} ({o.slug})</SelectItem>)}</SelectList>
  </SelectContent></Select><Input className="w-full sm:w-56" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={`Type ${app.slug} to confirm`} disabled={!target || transfer.isPending} /><Button variant="outline" size="xs" disabled={!target || confirm !== app.slug || !app.org_id || transfer.isPending} onClick={() => transfer.mutate()}>{transfer.isPending ? "Transferring…" : "Transfer"}</Button></div></div>;
}

function DefaultChannelPicker({
  appId,
  app,
  isOrgAdmin,
}: {
  appId: string;
  app:
    | (App & {
        default_channel_id?: string | null;
        default_channel_slug?: string | null;
        default_channel_name?: string | null;
      })
    | undefined;
  isOrgAdmin: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const channels = useQuery({
    queryKey: ["channels", appId],
    queryFn: () => listChannels(appId),
  });
  const [selected, setSelected] = useState<string | null>(
    app?.default_channel_id ?? null,
  );
  useEffect(() => {
    setSelected(app?.default_channel_id ?? null);
  }, [app?.default_channel_id]);

  const save = useMutation({
    mutationFn: () => updateApp(appId, { default_channel_id: selected }),
    onSuccess: () => {
      toast.show({
        kind: "success",
        title: selected
          ? "Default release channel updated"
          : "Default channel cleared (will fall back to first channel)",
      });
      qc.invalidateQueries({ queryKey: ["apps"] });
      qc.invalidateQueries({ queryKey: ["app-detail", appId] });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Save failed",
        description: (e as Error).message,
      }),
  });

  const dirty = selected !== (app?.default_channel_id ?? null);

  return (
    <div className="border-t border-line-hairline pt-3">
      <h3 className="text-sm font-medium text-foreground-strong mb-2">
        Default release channel
      </h3>
      <p className="text-xs text-foreground-muted mb-2">
        Pre-fills the channel dropdown in the New Release dialog. Falls
        back to the first channel (by created_at) if unset.
      </p>
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <Select
          items={{
            "": "— none (use first channel) —",
            ...Object.fromEntries(
              (channels.data?.channels ?? []).map((c: Channel) => [
                c.id,
                `${c.slug} (${c.name})`,
              ]),
            ),
          }}
          value={selected ?? ""}
          onValueChange={(v) => setSelected((v as string) || null)}
          disabled={!isOrgAdmin || save.isPending}
        >
          <SelectTrigger className="text-sm flex-1">
            <SelectValue />
            <SelectIcon />
          </SelectTrigger>
          <SelectContent>
            <SelectList>
            <SelectItem value="">— none (use first channel) —</SelectItem>
            {channels.data?.channels.map((c: Channel) => (
              <SelectItem key={c.id} value={c.id}>
                {c.slug} ({c.name})
              </SelectItem>
            ))}
          </SelectList>
          </SelectContent>
        </Select>
        {isOrgAdmin && (
          <Button
            variant="primary"
            size="xs"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        )}
      </div>
      {!isOrgAdmin && (
        <p className="text-xs text-warning-strong mt-2">
          ⚠ Org owner / admin required to change settings.
        </p>
      )}
    </div>
  );
}

function CreateChannelDialog({
  appId,
  onClose,
  onCreated,
}: {
  appId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [slug, setSlug] = useState("main");
  const [name, setName] = useState("Main");
  const [bundleId, setBundleId] = useState("");
  const toast = useToast();

  const create = useMutation({
    mutationFn: () =>
      createChannel(appId, {
        slug,
        name,
        bundle_id: bundleId.trim() || undefined,
      }),
    onSuccess: () => {
      toast.show({ kind: "success", title: `Channel '${slug}' created` });
      onCreated();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Failed to create channel",
        description: (e as Error).message,
      }),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New channel</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            id="create-channel-form"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
            className="space-y-3"
          >
            <div>
              <Label>Slug</Label>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label>Bundle ID override (optional)</Label>
              <Input
                className="font-mono text-xs"
                value={bundleId}
                onChange={(e) => setBundleId(e.target.value)}
                placeholder="com.example.myapp.beta"
              />
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-channel-form"
            variant="primary"
            disabled={create.isPending}
          >
            {create.isPending ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelRow({
  channel: c,
  onEdit,
  busy,
}: {
  channel: Channel;
  onEdit: () => void;
  busy: boolean;
}) {
  return (
    <section className="border-t border-line-hairline pt-3 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{c.name}</span>
          <span className="text-xs font-mono text-foreground-muted">{c.slug}</span>
          {c.bundle_id && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Badge variant="information" className="font-mono">
                    {c.bundle_id}
                  </Badge>
                }
              />
              <TooltipContent>Bundle ID override</TooltipContent>
            </Tooltip>
          )}
          {c.password && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Badge variant="warning">🔒 gated</Badge>
                }
              />
              <TooltipContent>Downloads require password</TooltipContent>
            </Tooltip>
          )}
          {c.git_url && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <a
                    className="text-xs text-info-strong hover:underline font-mono truncate max-w-xs"
                    href={c.git_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {c.git_url}
                  </a>
                }
              />
              <TooltipContent>{c.git_url}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      <Button
        variant="outline"
        onClick={onEdit}
        disabled={busy}
      >
        Edit
      </Button>
    </section>
  );
}

function EditChannelDialog({
  appId,
  app,
  channel,
  onClose,
  onSaved,
}: {
  appId: string;
  app:
    | (App & {
        default_channel_id?: string | null;
        default_channel_slug?: string | null;
        default_channel_name?: string | null;
      })
    | null
    | undefined;
  channel: Channel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(channel.name);
  const [bundleId, setBundleId] = useState(channel.bundle_id ?? "");
  const [password, setPassword] = useState(channel.password ?? "");
  const [gitUrl, setGitUrl] = useState(channel.git_url ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState("");

  const save = useMutation({
    mutationFn: () =>
      updateChannel(appId, channel.id, {
        name,
        bundle_id: bundleId.trim() || null,
        password: password.trim() || null,
        git_url: gitUrl.trim() || null,
      }),
    onSuccess: () => {
      toast.show({ kind: "success", title: `Channel '${channel.slug}' saved` });
      onSaved();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Save failed",
        description: (e as Error).message,
      }),
  });

  const remove = useMutation({
    mutationFn: () => deleteChannel(appId, channel.id),
    onSuccess: () => {
      toast.show({ kind: "success", title: `Channel '${channel.slug}' deleted` });
      onSaved();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Delete failed",
        description: (e as Error).message,
      }),
  });

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => { if (!open) onClose(); }}
      >
        {/* z-10 keeps this dialog below the nested delete-confirm (z-20),
            matching the original hand-rolled stacking. */}
        <DialogContent className="max-w-md z-10" overlay={{ className: "z-10" }}>
          <DialogHeader>
            <DialogTitle>Edit channel '{channel.slug}'</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              id="edit-channel-form"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
              className="space-y-3"
            >
              <div>
                <Label>Slug (immutable)</Label>
                <Input
                  className="font-mono text-xs bg-layer-inset"
                  value={channel.slug}
                  readOnly
                />
              </div>
              <div>
                <Label>Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Bundle ID override</Label>
                <Input
                  className="font-mono text-xs"
                  value={bundleId}
                  onChange={(e) => setBundleId(e.target.value)}
                  placeholder="com.example.myapp.beta"
                />
              </div>
              <div>
                <Label>Download password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="leave blank for no gate"
                />
              </div>
              <div>
                <Label>Git URL</Label>
                <Input
                  className="font-mono text-xs"
                  value={gitUrl}
                  onChange={(e) => setGitUrl(e.target.value)}
                  placeholder="https://github.com/foo/bar/tree/beta"
                />
              </div>
            </form>
          </DialogBody>
          <DialogFooter className="justify-between">
            <Button
              type="button"
              variant="danger-outline"
              onClick={() => setConfirmDelete(true)}
              disabled={save.isPending || remove.isPending}
            >
              Delete channel
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="edit-channel-form"
                variant="primary"
                disabled={save.isPending}
              >
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Channel delete confirmation (typed-confirm + default-channel warning) */}
      <ConfirmActionDialog
        open={confirmDelete}
        title="Delete channel?"
        objectLabel={channel.slug}
        objectHint={`id: ${channel.id.slice(0, 8)}…`}
        objectSummary={
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono">
            <div className="text-foreground-muted">slug</div>
            <div>{channel.slug}</div>
            <div className="text-foreground-muted">name</div>
            <div>{channel.name}</div>
            <div className="text-foreground-muted">bundle_id</div>
            <div>{channel.bundle_id ?? "—"}</div>
          </div>
        }
        body={
          <>
            Deleting this channel removes it from this app.{" "}
            <strong>
              All releases pointing at this channel must be cancelled or moved
              first
            </strong>{" "}
            — otherwise the public API will start 404'ing your clients.
            {app?.default_channel_id === channel.id && (
              <div className="mt-2 p-2 bg-warning-soft border border-warning/30 rounded-sm text-xs text-warning-strong">
                ⚠ This is the app's <strong>default release channel</strong>.
                After deletion, the New Release wizard will fall back to the
                first channel by created_at.
              </div>
            )}
            <TypedConfirmField
              required={channel.slug}
              value={typedConfirm}
              onChange={setTypedConfirm}
            />
          </>
        }
        confirmLabel="Delete channel"
        cancelLabel="Keep channel"
        confirmKind="danger"
        confirmDisabled={typedConfirm !== channel.slug}
        pending={remove.isPending}
        onCancel={() => {
          setConfirmDelete(false);
          setTypedConfirm("");
        }}
        onConfirm={() => {
          remove.mutate();
          setConfirmDelete(false);
          setTypedConfirm("");
        }}
      />
    </>
  );
}
